import { Options, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { CSSProperties, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { DeepNullable } from 'ts-essentials';

import { TextButton } from '@flipster/react/design-system/text-button';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { CompetitionIdContext, competitionEventsBunja, competitionTabsBunja } from '../state';

export const Rules = () => {
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { selectedEventTabRulesAtom } = useBunja(competitionEventsBunja);
  const selectedEventTabRules = useAtomValue(selectedEventTabRulesAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);

  if (selectedEventTabRules)
    return <CompetitionRichText key={selectedEventTabValue ?? 'default'} richText={selectedEventTabRules} />;
  return null;
};

interface RichTextProps {
  richText: DeepNullable<{ json: Document }>;
}

const maxLines = 11; // 최대 표시할 줄 수
const CompetitionRichText = ({ richText }: RichTextProps) => {
  const id = useContext(CompetitionIdContext);
  const [expanded, setExpanded] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const options = useMemo<Options>(
    () => ({
      renderNode: {
        [BLOCKS.HEADING_1]: (_node, children) => {
          return <h3>{children}</h3>;
        },
        [BLOCKS.HEADING_2]: (_node, children) => {
          return <h4>{children}</h4>;
        },
        [INLINES.HYPERLINK]: (node, children) => {
          if (node?.nodeType === 'hyperlink' && node?.data.uri.startsWith('https://flipster.io')) {
            const url = new URL(node?.data.uri);
            return <IsomorphicLink href={url.pathname + url.search + url.hash}>{children}</IsomorphicLink>;
          }
          if (node?.data.uri) {
            return (
              <IsomorphicLink href={node?.data.uri} newTab>
                {children}
              </IsomorphicLink>
            );
          }
          return children;
        },
      },
    }),
    [],
  );

  // 컨텐츠가 잘리는지 확인
  useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current;

      // lineClamp 없을 때 높이 측정
      const originalDisplay = element.style.display;
      element.style.display = 'block';
      const fullHeight = element.scrollHeight;

      // lineClamp 있을 때 높이 측정
      element.style.display = '-webkit-box';
      const clampedHeight = element.scrollHeight;

      // 원래 스타일 복원
      element.style.display = originalDisplay;

      // 두 높이가 다르면 버튼 표시
      setShowButton(fullHeight > clampedHeight);
    }
  }, [richText]);

  return (
    <div className={css({ my: 40 })}>
      <RulesStyleWrapper
        css={css.raw(
          !expanded &&
            showButton && {
              display: '-webkit-box',
              lineClamp: maxLines,
              overflow: 'hidden',
            },
        )}
        ref={contentRef}
      >
        {documentToReactComponents(richText.json as Document, options)}
      </RulesStyleWrapper>
      {showButton && (
        <div className={center({ w: '100%' })}>
          <TextButton
            size='medium'
            color='passive'
            onClick={() => {
              if (!expanded) {
                segment.track(CompetitionsEvent.CompetitionTradingRuleLearnMoreClick, { competition_id: id });
              }
              setExpanded((prevState) => !prevState);
            }}
            icon={<ChevronDownIcon />}
            label={expanded ? 'View less' : 'View more'}
            css={css.raw({ mt: 8, mx: 'auto' }, expanded && { '& svg': { transform: 'rotate(180deg)' } })}
          />
        </div>
      )}
    </div>
  );
};

export const RulesStyleWrapper = ({
  ref,
  css: cssProps,
  children,
}: PropsWithChildren<{ ref?: Ref<HTMLDivElement | null>; css?: Styles }>) => {
  return (
    <div
      ref={ref}
      className={css(
        {
          textStyle: 'Text/L/Regular',
          textAlign: 'left',
          color: 'content.default.level-3',
          overflow: 'hidden',
          '& h3': {
            textStyle: 'Title/S',
            color: 'content.default.level-5',
            mb: 12,
          },
          '& h4': {
            textStyle: 'Title/XS',
            color: 'content.default.level-3',
            mt: 22,
            mb: 12,
          },
          '& b': {
            textStyle: 'Text/L/Strong',
          },
          '& a': {
            textStyle: 'Text/L/Underline',
            color: 'content.accent.info',
          },
          '& ol, & ul': {
            m: 0,
            '& ul li': {
              listStyle: 'disc',
              ml: 12,
              pl: 4,
              mt: 4,
            },
          },
          '& ol': {
            counterReset: 'item',
          },
          '& > ol > li, & > ul > li': {
            pos: 'relative',
            pl: 46,
            py: 8,
            _before: {
              textStyle: 'Text/M/Monospace/Strong',
              color: 'content.default.level-3',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pos: 'absolute',
              top: 4,
              left: 0,
              w: 28,
              h: 28,
              rounded: 4,
              bg: 'background.default.level-1',
            },
          },
          '& > ol > li': {
            counterIncrement: 'item',
            _before: {
              content: 'counter(item)',
            },
          },
          '& > ul > li': {
            _before: {
              content: '""',
              backgroundImage: 'var(--rocket-icon)',
              bgSize: '12px 12px',
              bgPosition: 'center',
              bgRepeat: 'no-repeat',
            },
          },
        },
        cssProps,
      )}
      style={{ '--rocket-icon': rocketIcon } as CSSProperties}
    >
      {children}
    </div>
  );
};

const rocketIcon = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.875.75a7.861 7.861 0 0 0-6.218 3.042A3.375 3.375 0 0 0 .75 7.125a.375.375 0 0 0 .375.375h2.066c.384.486.823.926 1.309 1.31v2.065a.375.375 0 0 0 .375.375 3.376 3.376 0 0 0 3.333-3.907 7.862 7.862 0 0 0 3.042-6.218.375.375 0 0 0-.375-.375zm-4.17 2.954a1.125 1.125 0 1 1 1.59 1.592 1.125 1.125 0 0 1-1.59-1.592zM2.728 8.512a.375.375 0 0 1-.098.109 1.872 1.872 0 0 0-.755 1.504 1.873 1.873 0 0 0 1.504-.755.375.375 0 1 1 .601.448 2.621 2.621 0 0 1-2.511 1.026.375.375 0 0 1-.312-.313 2.622 2.622 0 0 1 1.025-2.512.375.375 0 0 1 .546.493z' fill='%23EEE'/%3E%3C/svg%3E")`;
